<script lang="ts">
  import { messages, type Message } from '../../stores/messages';
  import MessageInput from './MessageInput.svelte';
  import TypingIndicator from './TypingIndicator.svelte';
  import ConsentBanner from './ConsentBanner.svelte';
  import QuotaBadge from './QuotaBadge.svelte';
  import ConversationMenu from './ConversationMenu.svelte';
  import ReportDialog from './ReportDialog.svelte';
  import { subscribeToConversation } from '../../utils/cable';
  import { apiClient } from '@/utils/api';
  import { onDestroy, tick } from 'svelte';

  interface Props {
    conversationId: string;
    currentUserId: string;
    onClosed?: () => void;
  }

  let { conversationId, currentUserId, onClosed }: Props = $props();

  let subscription: { unsubscribe(): void } | null = null;
  let messagesContainer: HTMLDivElement | undefined = $state();
  let toastMessage = $state('');
  let reportOpen = $state(false);

  let conv = $derived($messages.currentConversation);
  let isPending = $derived(conv?.status === 'pending');
  let amInitiator = $derived(!!conv?.is_initiator);
  let showConsentBanner = $derived(isPending && !amInitiator);
  let showQuotaBadge = $derived(isPending && amInitiator);

  $effect(() => {
    if (conversationId) {
      loadConversation();
    }
  });

  async function loadConversation() {
    subscription?.unsubscribe();
    await messages.openConversation(conversationId);
    await tick();
    scrollToBottom();

    const sub = await subscribeToConversation(conversationId, {
      onNewMessage: (message: Message) => {
        if (message.sender.id !== currentUserId) {
          messages.addMessage(message);
          scrollToBottom();
        }
      },
      onTyping: ({ user_id, is_typing }: { user_id: string; is_typing: boolean }) => {
        if (user_id !== currentUserId) {
          messages.setTyping(user_id, is_typing);
        }
      },
    });
    subscription = sub;
  }

  onDestroy(() => {
    subscription?.unsubscribe();
    messages.closeConversation();
  });

  function scrollToBottom() {
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  function showToast(text: string) {
    toastMessage = text;
    setTimeout(() => { toastMessage = ''; }, 3500);
  }

  async function handleSend(content: string) {
    const result = await messages.sendMessage(content);
    if (!result.ok) {
      if (result.code === 'CONSENT_REQUIRED') {
        showToast('답장을 받기 전까지 더 보낼 수 없어요.');
      } else if (result.code === 'BLOCKED_USER') {
        showToast('이 사용자에게 메시지를 보낼 수 없습니다.');
      } else {
        showToast(result.message || '메시지 전송에 실패했어요.');
      }
      return;
    }
    await tick();
    scrollToBottom();
  }

  async function handleAccept() {
    const ok = await messages.acceptConversation(conversationId);
    if (ok) showToast('대화를 수락했습니다.');
  }

  async function handleDecline() {
    const ok = await messages.declineConversation(conversationId);
    if (ok) {
      showToast('요청을 거절했습니다.');
      onClosed?.();
    }
  }

  async function handleHide() {
    const ok = await messages.hideConversation(conversationId);
    if (ok) {
      showToast('대화를 숨겼습니다. 설정에서 다시 표시할 수 있어요.');
      onClosed?.();
    }
  }

  async function handleBlock() {
    if (!conv) return;
    const res = await apiClient.post('/api/v1/blocks', { user_id: conv.other_participant.id });
    if (res.success) {
      showToast(`${conv.other_participant.name}님을 차단했습니다.`);
      onClosed?.();
    } else {
      showToast(res.message || '차단에 실패했습니다.');
    }
  }

  function handleOpenReport() {
    reportOpen = true;
  }

  function formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return '오늘';
    if (date.toDateString() === yesterday.toDateString()) return '어제';
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  function shouldShowDate(index: number, list: Message[]): boolean {
    if (index === 0) return true;
    const current = new Date(list[index].created_at).toDateString();
    const prev = new Date(list[index - 1].created_at).toDateString();
    return current !== prev;
  }

  let inputDisabled = $derived(
    $messages.isSending ||
    (showQuotaBadge && (conv?.quota_remaining ?? 3) <= 0)
  );
</script>

<div class="h-full flex flex-col relative">
  {#if conv}
    <div class="p-4 border-b border-default bg-surface flex items-center gap-3">
      <div class="w-10 h-10 bg-primary-50 dark:bg-dark-tertiary rounded-full
                  flex items-center justify-center text-primary dark:text-dark-text-high font-semibold">
        {conv.other_participant.name.charAt(0).toUpperCase()}
      </div>
      <div class="flex-1 min-w-0">
        <h3 class="font-semibold text-content truncate">{conv.other_participant.name}</h3>
        {#if conv.other_participant.email}
          <p class="text-caption text-content-tertiary truncate">{conv.other_participant.email}</p>
        {/if}
      </div>
      <ConversationMenu onHide={handleHide} onBlock={handleBlock} onReport={handleOpenReport} />
    </div>

    {#if showConsentBanner}
      <ConsentBanner
        conversation={conv}
        onAccept={handleAccept}
        onDecline={handleDecline}
        onBlock={handleBlock}
        onReport={handleOpenReport}
      />
    {/if}

    {#if showQuotaBadge}
      <QuotaBadge remaining={conv.quota_remaining} />
    {/if}
  {/if}

  <div
    bind:this={messagesContainer}
    class="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-dark-primary"
  >
    {#if $messages.isConversationLoading && $messages.messages.length === 0}
      <div class="flex items-center justify-center h-full">
        <div class="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    {:else}
      {#if $messages.hasMoreMessages}
        <button
          onclick={() => messages.loadMoreMessages()}
          class="w-full py-2 text-body2 text-primary hover:text-primary-dark"
          disabled={$messages.isConversationLoading}
        >
          {$messages.isConversationLoading ? '로딩 중...' : '이전 메시지 불러오기'}
        </button>
      {/if}

      {#each $messages.messages as message, index (message.id)}
        {#if shouldShowDate(index, $messages.messages)}
          <div class="flex items-center gap-4 my-4">
            <div class="flex-1 h-px bg-gray-200 dark:bg-dark-border"></div>
            <span class="text-caption text-content-tertiary">{formatDate(message.created_at)}</span>
            <div class="flex-1 h-px bg-gray-200 dark:bg-dark-border"></div>
          </div>
        {/if}

        <div class="flex {message.is_mine ? 'justify-end' : 'justify-start'}">
          <div class="max-w-[70%] {message.is_mine ? 'order-2' : 'order-1'}">
            <div class="rounded-2xl px-4 py-2 {message.is_mine
              ? 'bg-primary text-white rounded-br-sm'
              : 'bg-surface text-content rounded-bl-sm border border-default'}">
              <p class="whitespace-pre-wrap break-words">{message.content}</p>
            </div>
            <div class="flex items-center gap-2 mt-1 {message.is_mine ? 'justify-end' : 'justify-start'}">
              <span class="text-caption text-content-tertiary">{formatTime(message.created_at)}</span>
              {#if message.is_mine}
                {#if message.status === 'sending'}
                  <span class="text-caption text-content-tertiary">전송 중...</span>
                {:else if message.status === 'failed'}
                  <span class="text-caption text-error">전송 실패</span>
                {:else if message.read_at}
                  <span class="text-caption text-primary">읽음</span>
                {/if}
              {/if}
            </div>
          </div>
        </div>
      {/each}

      {#if $messages.typingUsers.size > 0}
        <TypingIndicator />
      {/if}
    {/if}
  </div>

  <MessageInput onSend={handleSend} disabled={inputDisabled} />

  {#if toastMessage}
    <div class="absolute bottom-20 left-1/2 -translate-x-1/2 max-w-[90%] px-4 py-2 rounded-md bg-content text-white text-body2 shadow-lg z-30">
      {toastMessage}
    </div>
  {/if}

  {#if conv}
    <ReportDialog
      bind:open={reportOpen}
      targetType="User"
      targetId={conv.other_participant.id}
      label={`${conv.other_participant.name}님 신고하기`}
      onClose={() => (reportOpen = false)}
      onSubmitted={() => showToast('신고가 접수되었습니다.')}
    />
  {/if}
</div>
